// src/pages/HomePage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import products from '../data/products';
import './HomePage.css';

const HomePage = () => {
  const navigate = useNavigate();
  const featuredProducts = products.filter(p => p.isNew).slice(0, 4);
  const categories = [...new Set(products.map(p => p.category))];
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [slidesPerView, setSlidesPerView] = useState(4);
  const carouselRef = useRef(null);
  const intervalRef = useRef(null);
  const resizeTimeoutRef = useRef(null);

  const categoryIcons = {
    'weekly-special': '🏷️',
    'Flower': '🌿',
    'Exotic-Flower': '🌸',
    'Edibles': '🍬',
    'Concentrates': '⚗️',
    'cart': '🔋',
    'disposable-cart': '💨',
    'Pre-rolls': '🚬',
    'Shake': '🍃'
  };

  const categoryColors = {
    'weekly-special': '#ef1325',
    'Flower': '#10b981',
    'Exotic-Flower': '#8b5cf6',
    'Edibles': '#f59e0b',
    'Concentrates': '#3b82f6',
    'cart': '#06b6d4',
    'disposable-cart': '#6366f1',
    'Pre-rolls': '#ef4444',
    'Shake': '#78716c'
  };

  // Handle responsive slides per view
  const updateSlidesPerView = useCallback(() => {
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width >= 1200) return 4;
      if (width >= 992) return 3;
      if (width >= 768) return 2;
      if (width >= 576) return 2;
      return 1;
    }
    return 1;
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        setSlidesPerView(updateSlidesPerView());
      }, 150);
    };

    setSlidesPerView(updateSlidesPerView());
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [updateSlidesPerView]);

  const totalSlides = Math.ceil(categories.length / slidesPerView);

  const nextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto play carousel
  useEffect(() => {
    if (isAutoPlaying && totalSlides > 1) {
      intervalRef.current = setInterval(nextSlide, 4000);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isAutoPlaying, currentSlide, totalSlides]);

  const handleMouseEnter = () => {
    setIsAutoPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  const handleMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCategoryStats = (category) => {
    return products.filter(p => p.category === category).length;
  };

  // Touch/swipe support for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      nextSlide();
    }
    if (touchStart - touchEnd < -50) {
      prevSlide();
    }
  };

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero-section"
      style={{
          // backgroundImage: 'url("/images/moon.jpg")',
          backgroundImage: 'linear-gradient(rgba(7, 212, 48, 0.3), rgba(21, 170, 116, 0.3)), url("/images/imm1.png")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          boxShadow: '0 6px 25px rgba(0, 0, 0, 0.4)'
        }}
      >
        <div className="hero-overlay"></div>
        <div className="hero-container">
          <div className="hero-content">
            {/* <div className="hero-badge">
              <span className="badge-icon">✨</span>
              Washington DC's Trusted Source
            </div> */}
            <h1 className="hero-title">
              In & Out DC Dispensary
              <span className="title-accent">Delivery</span>
            </h1>
            <p className="hero-subtitle">
              Premium Cannabis • Fast Discreet Delivery • Washington DC.
            </p>
            <div className="hero-actions">
              <button
                onClick={() => handleNavigation('/products')}
                className="hero-btn primary-btn"
              >
                <span className="btn-icon">🛍️</span>
                Shop All Products
              </button>
              <button
                onClick={() => handleNavigation('/categories')}
                className="hero-btn secondary-btn"
              >
                <span className="btn-icon">📍</span>
                Browse Categories
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-section">
        <div className="section-container">
          <div className="section-header">
            <div className="header-content">
              <h2 className="section-title">Featured Products</h2>
              <p className="section-subtitle">New arrivals & customer favorites</p>
            </div>
            <button
              onClick={() => handleNavigation('/products')}
              className="view-all-btn"
            >
              View All Products
              <svg className="arrow-icon" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10m-5 5l5-5-5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="products-grid">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Carousel */}
      <section className="category-section">
        <div className="section-container">
          <div className="section-header">
            <div className="header-content">
              <h2 className="section-title">Shop by Category</h2>
              <p className="section-subtitle">Explore our curated collections</p>
            </div>
            {totalSlides > 1 && (
              <div className="carousel-controls">
                <button
                  className="carousel-btn prev-btn"
                  onClick={prevSlide}
                  aria-label="Previous categories"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
                <button
                  className="carousel-btn next-btn"
                  onClick={nextSlide}
                  aria-label="Next categories"
                >
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div
            className="category-carousel"
            ref={carouselRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${currentSlide * 100}%)`,
                transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="carousel-slide">
                  {categories
                    .slice(slideIndex * slidesPerView, (slideIndex + 1) * slidesPerView)
                    .map(category => {
                      const productCount = getCategoryStats(category);
                      const icon = categoryIcons[category] || '📦';
                      const color = categoryColors[category] || '#666';

                      return (
                        <button
                          key={category}
                          onClick={() => handleNavigation(`/category/${category}`)}
                          className="category-card"
                          style={{ '--category-color': color }}
                        >
                          <div className="card-header">
                            <div className="category-icon">{icon}</div>
                            <div className="category-arrow">
                              <svg viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            </div>
                          </div>

                          <div className="card-content">
                            <h3 className="category-name">
                              {category.replace('-', ' ')}
                            </h3>
                            <div className="category-stats">
                              <span className="stat-badge">
                                <svg viewBox="0 0 24 24" fill="none" className="stat-icon">
                                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {productCount} products
                              </span>
                            </div>
                          </div>

                          <div className="card-footer">
                            <span className="explore-text">Explore Collection</span>
                          </div>
                        </button>
                      );
                    })}
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Dots */}
          {totalSlides > 1 && (
            <div className="carousel-dots">
              {Array.from({ length: totalSlides }).map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section">
        <div className="section-container">
          <div className="section-header">
            <p className="section-subtitle">Experience the finest selection of premium cannabis products delivered to your door with speed and discretion.</p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                    stroke="currentColor" strokeWidth="1.5" />
                  <path d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1h1a1 1 0 001-1v-3h1a2 2 0 012 2v2a1 1 0 001 1h1"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Fast Delivery</h3>
              <p>Delivery within 1 hour across Washington DC</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 15v3m0 3h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.694-.833-2.464 0L4.346 19.5c-.77.833.192 2.5 1.732 2.5z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Premium Quality</h3>
              <p>Trusted suppliers. Verified potency. Quality assured.</p>
            </div>

            <div className="benefit-card">
              <div className="benefit-icon">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
              <h3>Secure & Discreet</h3>
              <p>Professional delivery with vehicles and discreet packaging</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;